/**
 * Sprint P32 · Density Matrix Editor
 * Per-lithology × weathering × domain × oxidation table with AI
 * recommendations and CP sign-off.
 */
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle, Sparkle, PencilSimple, ShieldCheck } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  buildDensityMatrix, getDensityMatrix, saveDensityMatrix,
  approveDensityRow, approveDensityAll,
} from "@/lib/api";
import { WizardCard, Pill } from "@/pages/wizard/_shared";


const STATUS_TONE = {
  approved: "green", draft: "slate", rejected: "red",
};


export default function DensityMatrixEditor({ projectId }) {
  const qc = useQueryClient();

  const matrixQ = useQuery({
    queryKey: ["density-matrix", projectId],
    queryFn:  () => getDensityMatrix(projectId),
    enabled:  !!projectId,
    staleTime: 15_000,
  });

  const buildMut = useMutation({
    mutationFn: () => buildDensityMatrix(projectId),
    onSuccess: (data) => {
      // Save immediately so the CP sees v1 in history
      saveMut.mutate({ rows: data.rows || [], note: "AI-generated" });
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e.message),
  });

  const saveMut = useMutation({
    mutationFn: (body) => saveDensityMatrix(projectId, body),
    onSuccess: () => {
      toast.success("Matrix saved");
      qc.invalidateQueries({ queryKey: ["density-matrix", projectId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e.message),
  });

  const approveRowMut = useMutation({
    mutationFn: ({ version, key, decision }) =>
      approveDensityRow(projectId, version, { key, decision }),
    onSuccess: () => {
      toast.success("Row updated");
      qc.invalidateQueries({ queryKey: ["density-matrix", projectId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e.message),
  });

  const approveAllMut = useMutation({
    mutationFn: (version) => approveDensityAll(projectId, version),
    onSuccess: () => {
      toast.success("All rows approved — CP sign-off recorded");
      qc.invalidateQueries({ queryKey: ["density-matrix", projectId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e.message),
  });

  const matrix = matrixQ.data || {};
  const rows = useMemo(() => matrix.rows || [], [matrix.rows]);

  const [edits, setEdits] = useState({}); // rowIdx → new assigned_value
  const commitEdit = (idx, val) => setEdits((e) => ({ ...e, [idx]: val }));

  const saveEdits = () => {
    const nextRows = rows.map((r, i) => (
      edits[i] != null
        ? { ...r, assigned_value: Number(edits[i]), source: "manual" }
        : r
    ));
    saveMut.mutate({ rows: nextRows, note: "manual edit" });
    setEdits({});
  };

  const approvalCount = useMemo(
    () => rows.filter((r) => r.approval_status === "approved").length, [rows]);
  const approvalPct = matrix.approval_pct ?? 0;

  // Sprint P32 · 4-D dimension pickers so CPs can override missing
  // weathering / domain / oxidation columns when the source geology CSV
  // did not carry them.
  const [showAdder, setShowAdder] = useState(false);
  const [newRow, setNewRow] = useState({
    lithology: "", weathering: "—", mineralised_domain: "—", oxidation: "—",
    ai_value: 2.7, assigned_value: 2.7,
  });

  const commitNewRow = () => {
    if (!newRow.lithology.trim()) {
      toast.error("Lithology is required.");
      return;
    }
    const litho = newRow.lithology.trim().toUpperCase();
    const dupe = rows.some((r) =>
      r.key.lithology === litho &&
      (r.key.weathering || "—") === (newRow.weathering || "—") &&
      (r.key.mineralised_domain || "—") === (newRow.mineralised_domain || "—") &&
      (r.key.oxidation || "—") === (newRow.oxidation || "—"));
    if (dupe) {
      toast.error("A row with those 4 dimensions already exists.");
      return;
    }
    const row = {
      key: {
        lithology: litho,
        weathering: (newRow.weathering || "—").toUpperCase(),
        mineralised_domain: (newRow.mineralised_domain || "—").toUpperCase(),
        oxidation: (newRow.oxidation || "—").toUpperCase(),
      },
      n_samples: 0, mean: null, median: null, std: null, min: null, max: null,
      ai_value: Number(newRow.ai_value),
      ai_rationale: "manual entry",
      assigned_value: Number(newRow.assigned_value),
      source: "manual",
      approval_status: "draft",
      approved_by: null, approved_at: null,
    };
    saveMut.mutate({ rows: [...rows, row], note: "manual 4-D row added" });
    setShowAdder(false);
    setNewRow({ lithology: "", weathering: "—", mineralised_domain: "—", oxidation: "—",
                 ai_value: 2.7, assigned_value: 2.7 });
  };

  return (
    <WizardCard title="Density Assignment"
                subtitle="Per lithology × weathering × domain × oxidation — AI preliminary until CP-approved"
                testId="w-density-matrix">
      <div className="flex items-center gap-2 mb-3" data-testid="w-density-matrix-toolbar">
        <Button size="sm"
                onClick={() => buildMut.mutate()}
                disabled={buildMut.isPending || saveMut.isPending}
                className="bg-brand-blue text-white hover:bg-brand-blue/90"
                data-testid="w-density-matrix-build">
          <Sparkle size={11} className="mr-1" />
          {buildMut.isPending ? "Building…" : "Rebuild from AI"}
        </Button>
        {rows.length > 0 && Object.keys(edits).length > 0 && (
          <Button size="sm" variant="outline" onClick={saveEdits}
                  data-testid="w-density-matrix-save-edits">
            <PencilSimple size={11} className="mr-1" />
            Save {Object.keys(edits).length} edit{Object.keys(edits).length === 1 ? "" : "s"}
          </Button>
        )}
        {rows.length > 0 && matrix.version > 0 && approvalPct < 100 && (
          <Button size="sm"
                  onClick={() => approveAllMut.mutate(matrix.version)}
                  disabled={approveAllMut.isPending}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white ml-auto"
                  data-testid="w-density-matrix-approve-all">
            <ShieldCheck size={11} weight="fill" className="mr-1" />
            Approve All ({approvalCount}/{rows.length})
          </Button>
        )}
        {matrix.approval_pct === 100 && (
          <Pill tone="green" testId="w-density-matrix-approved-pill">
            CP-approved · v{matrix.version}
          </Pill>
        )}
        <Button size="sm" variant="outline"
                onClick={() => setShowAdder((s) => !s)}
                data-testid="w-density-matrix-add-row-toggle"
                className="ml-auto">
          {showAdder ? "Cancel" : "+ Add 4-D row"}
        </Button>
      </div>

      {showAdder && (
        <div className="rounded border border-brand-blue/30 bg-brand-blue/5 p-3 mb-3"
              data-testid="w-density-matrix-adder">
          <div className="text-[10px] font-mono uppercase tracking-widest text-brand-blue mb-2">
            Add / override a 4-D density row
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <div>
              <label className="text-[10px] text-slate-600">Lithology *</label>
              <Input value={newRow.lithology}
                     onChange={(e) => setNewRow({ ...newRow, lithology: e.target.value })}
                     className="h-8 text-[12px] font-mono uppercase"
                     placeholder="e.g. GRN"
                     data-testid="w-density-matrix-add-litho" />
            </div>
            <div>
              <label className="text-[10px] text-slate-600">Weathering</label>
              <select className="h-8 w-full rounded border border-slate-300 px-1 text-[12px] font-mono"
                      value={newRow.weathering}
                      onChange={(e) => setNewRow({ ...newRow, weathering: e.target.value })}
                      data-testid="w-density-matrix-add-weather">
                {["—", "FRESH", "TRANSITION", "OX"].map((v) => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-slate-600">Domain</label>
              <select className="h-8 w-full rounded border border-slate-300 px-1 text-[12px] font-mono"
                      value={newRow.mineralised_domain}
                      onChange={(e) => setNewRow({ ...newRow, mineralised_domain: e.target.value })}
                      data-testid="w-density-matrix-add-domain">
                {["—", "VEIN", "HALO", "WASTE", "MINERALISED"].map((v) => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-slate-600">Oxidation</label>
              <select className="h-8 w-full rounded border border-slate-300 px-1 text-[12px] font-mono"
                      value={newRow.oxidation}
                      onChange={(e) => setNewRow({ ...newRow, oxidation: e.target.value })}
                      data-testid="w-density-matrix-add-oxidation">
                {["—", "OX", "REDOX", "SULFIDE", "FRESH"].map((v) => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-slate-600">Assigned (t/m³)</label>
              <Input type="number" step="0.01"
                     value={newRow.assigned_value}
                     onChange={(e) => setNewRow({ ...newRow, assigned_value: e.target.value })}
                     className="h-8 text-[12px] font-mono"
                     data-testid="w-density-matrix-add-value" />
            </div>
            <div className="flex items-end">
              <Button size="sm" onClick={commitNewRow}
                      className="bg-brand-blue text-white h-8"
                      data-testid="w-density-matrix-add-confirm">
                Add row
              </Button>
            </div>
          </div>
        </div>
      )}

      {matrixQ.isLoading && <div className="text-[11px] text-slate-500">Loading matrix…</div>}

      {!matrixQ.isLoading && rows.length === 0 && (
        <div className="rounded border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900"
              data-testid="w-density-matrix-empty">
          No matrix yet. Click <b>Rebuild from AI</b> to auto-generate a per-lithology density recommendation from your uploaded geology data.
        </div>
      )}

      {rows.length > 0 && (
        <div className="rounded border border-slate-200 overflow-x-auto"
              data-testid="w-density-matrix-table">
          <table className="w-full text-[11px] font-mono">
            <thead className="bg-slate-100 text-slate-700">
              <tr>
                <th className="text-left px-2 py-1">Lithology</th>
                <th className="text-left px-2 py-1">Weathering</th>
                <th className="text-left px-2 py-1">Domain</th>
                <th className="text-left px-2 py-1">Oxidation</th>
                <th className="text-right px-2 py-1" title="Measured density samples">n</th>
                <th className="text-right px-2 py-1">Mean</th>
                <th className="text-right px-2 py-1">AI</th>
                <th className="text-right px-2 py-1">Assigned</th>
                <th className="text-center px-2 py-1">Source</th>
                <th className="text-center px-2 py-1">Status</th>
                <th className="text-center px-2 py-1">CP</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const dirty = edits[i] != null;
                const val = edits[i] ?? r.assigned_value;
                return (
                  <tr key={i}
                      data-testid={`w-density-matrix-row-${i}`}
                      className={"border-t border-slate-200 " +
                        (dirty ? "bg-sky-50" :
                         r.approval_status === "approved" ? "bg-emerald-50/40" : "")}>
                    <td className="px-2 py-1">{r.key.lithology}</td>
                    <td className="px-2 py-1 text-slate-500">{r.key.weathering}</td>
                    <td className="px-2 py-1 text-slate-500">{r.key.mineralised_domain}</td>
                    <td className="px-2 py-1 text-slate-500">{r.key.oxidation}</td>
                    <td className="text-right px-2 py-1">{r.n_samples}</td>
                    <td className="text-right px-2 py-1 text-slate-600">
                      {r.mean != null ? r.mean.toFixed(2) : "—"}
                    </td>
                    <td className="text-right px-2 py-1 text-brand-blue"
                        title={r.ai_rationale}>
                      {r.ai_value.toFixed(2)}
                    </td>
                    <td className="text-right px-2 py-1">
                      <Input type="number" step="0.01" value={val}
                              onChange={(e) => commitEdit(i, e.target.value)}
                              className="h-6 w-16 text-[10px] font-mono text-right ml-auto px-1 py-0"
                              data-testid={`w-density-matrix-value-${i}`} />
                    </td>
                    <td className="text-center px-2 py-1">
                      <span className={"text-[9px] px-1.5 py-0.5 rounded uppercase " +
                        ({measured: "bg-emerald-100 text-emerald-700",
                          ai:       "bg-sky-100 text-sky-700",
                          manual:   "bg-amber-100 text-amber-700",
                          default:  "bg-slate-100 text-slate-600"}[r.source] || "bg-slate-100 text-slate-600")}>
                        {r.source}
                      </span>
                    </td>
                    <td className="text-center px-2 py-1">
                      <Pill tone={STATUS_TONE[r.approval_status] || "slate"}>
                        {r.approval_status}
                      </Pill>
                    </td>
                    <td className="text-center px-2 py-1">
                      {r.approval_status === "approved" ? (
                        <div className="flex items-center justify-center gap-1 text-emerald-600 text-[10px]"
                             title={`Approved by ${r.approved_by} @ ${r.approved_at}`}>
                          <CheckCircle size={10} weight="fill" /> {r.approved_by?.split("@")[0] || "—"}
                        </div>
                      ) : (
                        <button
                          onClick={() => approveRowMut.mutate({
                            version: matrix.version, key: r.key, decision: "approved",
                          })}
                          data-testid={`w-density-matrix-approve-${i}`}
                          className="text-[10px] px-1.5 py-0.5 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                          Approve
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {matrix.approval_pct === 100 && matrix.cp_signed_off_by && (
        <div className="mt-3 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2 flex items-center gap-2"
              data-testid="w-density-matrix-signed">
          <ShieldCheck size={13} weight="fill" className="text-emerald-600" />
          <span>
            Fully approved by <b>{matrix.cp_signed_off_by}</b> at {matrix.cp_signed_off_at?.slice?.(0, 19)?.replace?.("T", " ")}
            {matrix.effective_model_version && (
              <> · effective for block-model v<b>{matrix.effective_model_version}</b></>
            )}
          </span>
        </div>
      )}
    </WizardCard>
  );
}
